import {
  FC,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useState,
  CSSProperties,
  ChangeEventHandler,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useWavesurfer } from '@wavesurfer/react';
import { useTheme } from 'styled-components/macro';
import { formatTimer } from 'utils';
import { Icons } from 'assets';
import { HStack, SVG } from 'components';
import { Button, Dropdown, Text } from 'components/ui';
import { useStore } from 'hooks';
import { VolumeControl, WaveContainer } from './styled';

/** Генерирация фейковой волны для визуализации. */
const generateFakePeaks = (length = 100): Float32Array => {
  const peaks = new Float32Array(length);

  for (let i = 0; i < length; i++) {
    peaks[i] = 0.3 + Math.random() * 0.7;
  }

  return peaks;
};

type AudioPlayerWaveProps = {
  record_url: string;
};

/**
 * Wavesurfer React: https://github.com/katspaugh/wavesurfer-react
 * Wavesurfer Docs: https://wavesurfer.xyz/docs/
 * TODO: необходимо разрешить фетчить файлы по record_url, сейчас это не работает из-за CORS.
 * Сейчас используется стриминг файла через MediaElement, чтобы не было CORS-запросов, как в старом аудиоплеере.
 * Реальный waveform отобразить без разблокировки CORS-запросов и подробного анализа файла либой невозможно.
 */
export const AudioPlayerWave: FC<AudioPlayerWaveProps> = observer(
  ({ record_url }) => {
    const { telephony } = useStore();
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    /** Создание Audio элемент напрямую как в старом аудиоплеере — он будет стримить без CORS-запросов. */
    const audioRef = useRef<HTMLAudioElement | null>(null);

    if (!audioRef.current) {
      audioRef.current = new Audio(record_url);
    }

    const fakePeaks = useMemo(() => [generateFakePeaks(100)], []);

    const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
      container: containerRef,
      height: 24,
      waveColor: theme.colors.gray[300],
      progressColor: theme.colors.primary[500],
      width: '100%',
      barWidth: 2,
      barGap: 2,
      barRadius: 999,
      cursorColor: 'transparent',
      /**
       *  Media элемент напрямую — без fetch запросов.
       *  Фейковые peaks, чтобы wavesurfer не пытался загружать файл для анализа.
       *  Duration как предохранитель от ошибки стриминга файла.
       *  После разблокировки CORS-запросов, необходимо будет удалить ключи ниже, вместо них добавить:
       *  url: record_url,
       */
      media: audioRef.current,
      peaks: fakePeaks,
      duration: audioRef.current.duration,
    });

    const [volume, setVolume] = useState(telephony.audioVolume);

    /** После окончания воспроизведения возвращаем позицию в начало, чтобы волна стала снова серой. */
    useEffect(() => {
      if (!wavesurfer) {
        return;
      }

      const unsubscribe = wavesurfer.on('finish', () => {
        // Возвращаемся в начало, чтобы убрать прогресс-цвет.
        wavesurfer.seekTo(0);
        wavesurfer.pause();

        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      });

      return () => unsubscribe();
    }, [wavesurfer]);

    const handleVolumeChange: ChangeEventHandler<HTMLInputElement> =
      useCallback(
        (e) => {
          const newVolume = Number(e.target.value);

          setVolume(newVolume);
          telephony.setAudioVolume(newVolume);
          wavesurfer?.setVolume(newVolume);
        },
        [telephony, wavesurfer]
      );

    /** Синхронизация громкости при изменении глобального значения. */
    useEffect(() => {
      setVolume(telephony.audioVolume);
      wavesurfer?.setVolume(telephony.audioVolume);
    }, [telephony.audioVolume, wavesurfer]);

    /** Старт/пауза с синхронизацией через telephonyStore,
     *  чтобы в один момент играла только одна запись.
     */
    const onPlayPause = useCallback(() => {
      if (!wavesurfer) {
        return;
      }

      if (isPlaying) {
        wavesurfer.pause();
        telephony.setIsNowPlaying(null);
      } else {
        telephony.setIsNowPlaying(record_url);
        wavesurfer.play();
      }
    }, [isPlaying, record_url, telephony, wavesurfer]);

    /** Клик по волне использует ту же логику, что и кнопка play/pause. */
    useEffect(() => {
      if (!wavesurfer) {
        return;
      }

      const unsubscribe = wavesurfer.on('click', () => {
        onPlayPause();
      });

      return () => unsubscribe();
    }, [wavesurfer, onPlayPause]);

    /** Если в telephonyStore поменялся активный трек,
     *  останавливаем текущий плеер.
     */
    useEffect(() => {
      if (!wavesurfer) {
        return;
      }

      if (telephony.isNowPlaying !== record_url && isPlaying) {
        wavesurfer.pause();
      }
    }, [isPlaying, record_url, telephony.isNowPlaying, wavesurfer, telephony]);

    /**
     *  Из-за CORS ограничений прямое скачивание через blob невозможно.
     *  Как и прежде, файл в новой вкладке, где пользователь может скачать через встроенный плеер браузера.
     *  ТЗ:
     *  Нажатие → появляется уведомление «Формируем аудиофайл».
     *  При успехе: появляется уведомление: «Аудиофайл сформирован».
     *  При ошибке: появляется уведомление: «Упс...Что-то пошло не так, повторите попытку».
     *  На данный момент неосуществимо. Задать нормальный кастом нэйм файла пока также невозможно.
     */
    const downloadAudio = useCallback(() => {
      window.open(record_url, '_blank');
    }, [record_url]);

    return (
      <HStack
        gap={theme.spacings.base}
        align="center"
        style={{ width: '100%', maxWidth: '620px' }}
      >
        <HStack
          gap={theme.spacings.xs}
          align="center"
          style={{ flex: 1, minWidth: '100px', maxWidth: '532px' }}
        >
          <Button
            mode="neutral"
            variant="main"
            size="s"
            onClick={onPlayPause}
            iconButton={
              <SVG src={Icons[isPlaying ? 'CirclePause' : 'CirclePlay']} />
            }
          />
          <WaveContainer ref={containerRef} />
          <Text variant="numbers-13" color="gray.500" whiteSpace="nowrap">
            {formatTimer(Math.round(currentTime), false)}/
            {formatTimer(Math.round(wavesurfer?.getDuration() || 0), false)}
          </Text>
        </HStack>
        <HStack gap={theme.spacings.xs} align="center">
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button
                mode="neutral"
                variant="main"
                size="s"
                iconButton={<SVG src={Icons.VolumeMedium} />}
              />
            </Dropdown.Trigger>
            <Dropdown.Content side="top" style={{ padding: '4px' }}>
              <VolumeControl
                value={volume}
                onChange={handleVolumeChange}
                style={{ '--value': volume } as CSSProperties}
              />
            </Dropdown.Content>
          </Dropdown.Root>
          <Button
            mode="neutral"
            variant="main"
            size="s"
            onClick={downloadAudio}
            iconButton={<SVG src={Icons.ArrowDownBottomLine} />}
          />
        </HStack>
      </HStack>
    );
  }
);
